import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import categorywisebgimage from "../../assets/Images/Categorypagebgimage.webp";
import {
  AiOutlineArrowLeft,
  AiOutlineHeart,
  AiFillHeart,
  AiOutlineShoppingCart,
} from "react-icons/ai";
import { MdOutlineBarChart } from "react-icons/md";
import { FaStar, FaStarHalfAlt, FaRegStar } from "react-icons/fa";
import ProductSkeleton from "../Home/ProductSkeleton";
import { toast } from "react-toastify";

const CategoryWiseProduct = () => {
  const navigate = useNavigate();
  const { categoryId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoryData, setCategoryData] = useState([]);
  const [categoryName, setCategoryName] = useState("");
  const [selectedSubCategory, setSelectedSubCategory] = useState("all");
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [addingToCart, setAddingToCart] = useState({});
  const [wishlist, setWishlist] = useState([]);
  const [comparison, setComparison] = useState([]);
  const [processingWishlist, setProcessingWishlist] = useState({});
  const [processingComparison, setProcessingComparison] = useState({});
  const [userData, setUserData] = useState(null);
  const [allCategories, setAllCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState("all");
  const [availableBrands, setAvailableBrands] = useState([]);

  useEffect(() => {
    const getUserData = async () => {
      // Get user data from localStorage
      const storedUserData = localStorage.getItem("BricksNbar-Users-Data");
      if (storedUserData) {
        const parsedUserData = JSON.parse(storedUserData);
        setUserData(parsedUserData);
        fetchWishlist(parsedUserData._id);
        fetchComparison(parsedUserData._id);
      }
    };

    getUserData();
    fetchCategoryData();
    fetchAllCategories();
  }, [categoryId]);

  // Update available brands whenever subcategory or products change
  useEffect(() => {
    updateAvailableBrands();
  }, [selectedSubCategory, allProducts]);

  const fetchAllCategories = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/categories/get-all-categories`
      );
      setAllCategories(response.data);
    } catch (err) {
      console.error("Failed to fetch all categories", err);
    }
  };

  const updateAvailableBrands = () => {
    let productsToFilter = [];

    if (selectedSubCategory === "all") {
      productsToFilter = allProducts;
    } else {
      const subCategoryData = categoryData.find(
        (item) => item.subCategory._id === selectedSubCategory
      );
      if (subCategoryData) {
        productsToFilter = subCategoryData.products;
      }
    }

    // Extract unique brands from filtered products
    const uniqueBrands = [
      ...new Set(
        productsToFilter.map((product) => product.brand && product.brand.trim())
      ),
    ].filter(Boolean);

    // Convert to brand objects format
    const formattedBrands = uniqueBrands.map((brand) => ({
      _id: brand,
      name: brand,
    }));

    setAvailableBrands(formattedBrands);

    // Reset selected brand if it's not available in current subcategory
    const brandExists = formattedBrands.some(
      (brand) => brand._id === selectedBrand
    );
    if (!brandExists && selectedBrand !== "all") {
      setSelectedBrand("all");
    }
  };

  const fetchWishlist = async (userId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/wishlist/wishlist-by-user/${userId}`
      );

      if (response.data && response.data.products) {
        const wishlistProductIds = response.data.products.map(
          (product) => product._id
        );
        setWishlist(wishlistProductIds);
      }
    } catch (error) {
      console.error("Error fetching wishlist:", error);
    }
  };

  const fetchComparison = async (userId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/comparison/comparison-by-user/${userId}`
      );

      if (response.data && response.data.products) {
        const comparisonProductIds = response.data.products.map(
          (product) => product._id
        );
        setComparison(comparisonProductIds);
      }
    } catch (error) {
      console.error("Error fetching comparison list:", error);
    }
  };

  // Helper function to get price information based on product size type
  const getPriceInfo = (product) => {
    if (product.sizeType === "one" && product.singleSize) {
      return {
        price: product.singleSize.price,
        previousPrice: product.singleSize.previous_price,
      };
    } else if (
      product.sizeType === "multiple" &&
      product.multipleSizes &&
      product.multipleSizes.length > 0
    ) {
      // For multiple sizes, show the first size's price or you can show a range
      const firstSize = product.multipleSizes[0];
      return {
        price: firstSize.price,
        previousPrice: firstSize.previous_price,
      };
    }
    return {
      price: 0,
      previousPrice: null,
    };
  };

  const fetchCategoryData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/products/sub-categories-products-by-category/${categoryId}`
      );

      setCategoryData(response.data);

      const allProductsList = [];
      response.data.forEach((item) => {
        allProductsList.push(...item.products);
      });
      setAllProducts(allProductsList);

      setProducts(allProductsList);

      fetchCategoryName(categoryId);
      setLoading(false);
    } catch (err) {
      setError("Failed to fetch category data");
      setLoading(false);
      console.error(err);
    }
  };

  const fetchCategoryName = async (catId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/categories/get-category-by-id/${catId}`
      );
      setCategoryName(response.data.name);
    } catch (err) {
      console.error("Failed to fetch category name", err);
      setCategoryName("Category");
    }
  };

  const handleSubCategoryClick = (subCategoryId) => {
    setSelectedSubCategory(subCategoryId);
    setSelectedBrand("all"); // Reset brand selection when changing subcategory

    if (subCategoryId === "all") {
      setProducts(allProducts);
    } else {
      const subCategoryData = categoryData.find(
        (item) => item.subCategory._id === subCategoryId
      );
      if (subCategoryData) {
        setProducts(subCategoryData.products);
      }
    }
  };

  const handleBrandClick = (brandId) => {
    setSelectedBrand(brandId);

    if (brandId === "all") {
      // If subcategory is selected, apply that filter
      if (selectedSubCategory !== "all") {
        const subCategoryData = categoryData.find(
          (item) => item.subCategory._id === selectedSubCategory
        );
        if (subCategoryData) {
          setProducts(subCategoryData.products);
        }
      } else {
        setProducts(allProducts);
      }
    } else {
      // Filter by brand
      let filteredProducts;

      if (selectedSubCategory !== "all") {
        // Filter by both subcategory and brand
        const subCategoryData = categoryData.find(
          (item) => item.subCategory._id === selectedSubCategory
        );

        if (subCategoryData) {
          filteredProducts = subCategoryData.products.filter(
            (product) => product.brand && product.brand.trim() === brandId
          );
        }
      } else {
        // Filter only by brand
        filteredProducts = allProducts.filter(
          (product) => product.brand && product.brand.trim() === brandId
        );
      }

      setProducts(filteredProducts || []);
    }
  };

  const handleCategoryClick = (catId) => {
    navigate(`/allcategories/${catId}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const renderRatingStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating - fullStars >= 0.5;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<FaStar key={`full-${i}`} className="h-4 w-4" />);
    }

    if (hasHalfStar) {
      stars.push(<FaStarHalfAlt key="half" className="h-4 w-4" />);
    }

    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<FaRegStar key={`empty-${i}`} className="h-4 w-4" />);
    }

    return stars;
  };

  const handleNavigateToProductDetails = (productId) => {
    navigate(`/product-details/${productId}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Wishlist functions
  const isInWishlist = (productId) => {
    return wishlist.includes(productId);
  };

  const handleAddToWishlist = async (event, productId) => {
    event.stopPropagation();

    if (!userData) {
      navigate("/sign-in");
      return;
    }

    try {
      setProcessingWishlist({ ...processingWishlist, [productId]: true });

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/wishlist/add-to-wishlist`,
        {
          userId: userData._id,
          productId: productId,
        }
      );

      if (response.data) {
        setWishlist([...wishlist, productId]);
        // toast.success("Product added to wishlist!");
      }
    } catch (error) {
      console.error("Error adding to wishlist:", error);
      toast.error("Failed to add to wishlist");
    } finally {
      setProcessingWishlist({ ...processingWishlist, [productId]: false });
    }
  };

  const handleRemoveFromWishlist = async (event, productId) => {
    event.stopPropagation();

    if (!userData) {
      navigate("/sign-in");
      return;
    }

    try {
      setProcessingWishlist({ ...processingWishlist, [productId]: true });

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/wishlist/remove-from-wishlist`,
        {
          userId: userData._id,
          productId: productId,
        }
      );

      if (response.data) {
        setWishlist(wishlist.filter((id) => id !== productId));
        // toast.success("Product removed from wishlist!");
      }
    } catch (error) {
      console.error("Error removing from wishlist:", error);
      toast.error("Failed to remove from wishlist");
    } finally {
      setProcessingWishlist({ ...processingWishlist, [productId]: false });
    }
  };

  const handleToggleWishlist = (event, productId) => {
    if (isInWishlist(productId)) {
      handleRemoveFromWishlist(event, productId);
    } else {
      handleAddToWishlist(event, productId);
    }
  };

  // Comparison functions
  const isInComparison = (productId) => {
    return comparison.includes(productId);
  };

  const handleAddToComparison = async (event, productId) => {
    event.stopPropagation();

    if (!userData) {
      navigate("/sign-in");
      return;
    }

    try {
      setProcessingComparison({ ...processingComparison, [productId]: true });

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/comparison/add-to-comparison`,
        {
          userId: userData._id,
          productId: productId,
        }
      );

      if (response.data) {
        setComparison([...comparison, productId]);
        toast.success("Product added to comparison!");
      }
    } catch (error) {
      console.error("Error adding to comparison:", error);
      toast.error("Failed to add to comparison");
    } finally {
      setProcessingComparison({ ...processingComparison, [productId]: false });
    }
  };

  const handleRemoveFromComparison = async (event, productId) => {
    event.stopPropagation();

    if (!userData) {
      navigate("/sign-in");
      return;
    }

    try {
      setProcessingComparison({ ...processingComparison, [productId]: true });

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/comparison/remove-from-comparison`,
        {
          userId: userData._id,
          productId: productId,
        }
      );

      if (response.data) {
        setComparison(comparison.filter((id) => id !== productId));
        toast.success("Product removed from comparison!");
      }
    } catch (error) {
      console.error("Error removing from comparison:", error);
      toast.error("Failed to remove from comparison");
    } finally {
      setProcessingComparison({ ...processingComparison, [productId]: false });
    }
  };

  const handleToggleComparison = (event, productId) => {
    if (isInComparison(productId)) {
      handleRemoveFromComparison(event, productId);
    } else {
      handleAddToComparison(event, productId);
    }
  };

  if (loading) {
    return <ProductSkeleton />;
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-red-500 text-lg">{error}</div>
      </div>
    );
  }

  return (
    <div className="relative mb-4">
      <div
        className="h-80 w-full bg-cover bg-center"
        style={{ backgroundImage: `url(${categorywisebgimage})` }}
      >
        <div className="absolute top-40 left-24 transform -translate-y-1/2">
          <h1 className="text-4xl font-bold text-white">{categoryName}</h1>
        </div>
      </div>

      {/* Categories Navigation Bar */}
      <div className="border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between overflow-x-auto py-4 gap-8">
            {allCategories.map((category) => (
              <div
                key={category._id}
                className={`flex flex-col items-center min-w-max cursor-pointer ${
                  category._id === categoryId
                    ? "text-orange-700 font-medium border-b-[5px] border-orange-600"
                    : "text-gray-700 hover:text-orange-600"
                }`}
                onClick={() => handleCategoryClick(category._id)}
              >
                <p className="text-md whitespace-nowrap">{category.name}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="container mx-auto mt-8 px-4">
        <div className="flex flex-col md:flex-row gap-8">
          <div className="md:w-1/4">
            {/* Subcategories Section */}
            <div className="mb-8">
              <h3 className="text-lg font-medium mb-4">Sub-categories</h3>
              <ul className="space-y-2 border-l-2 border-gray-200">
                <li>
                  <button
                    onClick={() => handleSubCategoryClick("all")}
                    className={`pl-4 py-2 w-full text-left transition-colors ${
                      selectedSubCategory === "all"
                        ? "border-l-4 border-red-500 -ml-0.5 font-medium text-red-500"
                        : "hover:text-red-500"
                    }`}
                  >
                    All Products
                  </button>
                </li>

                {categoryData.map((item) => (
                  <li key={item.subCategory._id}>
                    <button
                      onClick={() =>
                        handleSubCategoryClick(item.subCategory._id)
                      }
                      className={`pl-4 py-2 w-full text-left transition-colors ${
                        selectedSubCategory === item.subCategory._id
                          ? "border-l-4 border-red-500 -ml-0.5 font-medium text-red-500"
                          : "hover:text-red-500"
                      }`}
                    >
                      <span className="capitalize">
                        {item.subCategory.name}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Brands Section - Now shows only brands from selected subcategory */}
            <div>
              <h3 className="text-lg font-medium mb-4">Brands</h3>
              <ul className="space-y-2 border-l-2 border-gray-200">
                <li>
                  <button
                    onClick={() => handleBrandClick("all")}
                    className={`pl-4 py-2 w-full text-left transition-colors ${
                      selectedBrand === "all"
                        ? "border-l-4 border-orange-500 -ml-0.5 font-medium text-orange-500"
                        : "hover:text-orange-500"
                    }`}
                  >
                    All Brands
                  </button>
                </li>

                {availableBrands.map((brand) => (
                  <li key={brand._id}>
                    <button
                      onClick={() => handleBrandClick(brand._id)}
                      className={`pl-4 py-2 w-full text-left transition-colors ${
                        selectedBrand === brand._id
                          ? "border-l-4 border-orange-500 -ml-0.5 font-medium text-orange-500"
                          : "hover:text-orange-500"
                      }`}
                    >
                      <span className="capitalize">{brand.name}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="md:w-3/4">
            <div className="flex justify-between sm:items-center items-start mb-6 flex-col-reverse sm:flex-row gap-2 sm:gap-0">
              <div className="flex justify-start items-center gap-2">
                <div
                  className="p-2 rounded-full bg-gray-200 hover:bg-gray-700 hover:text-white cursor-pointer"
                  onClick={() => {
                    navigate(-1);
                  }}
                >
                  <AiOutlineArrowLeft size={24} />
                </div>

                <h2 className="text-xl font-semibold capitalize">
                  {selectedSubCategory === "all"
                    ? `All Products In ${categoryName}`
                    : `Products In ${
                        categoryData.find(
                          (item) => item.subCategory._id === selectedSubCategory
                        )?.subCategory.name || ""
                      }`}
                  {selectedBrand !== "all" &&
                    ` - ${
                      availableBrands.find(
                        (brand) => brand._id === selectedBrand
                      )?.name || ""
                    }`}
                </h2>
              </div>
            </div>

            {products.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">
                  No products found in this category
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {products.map((product) => {
                  const priceInfo = getPriceInfo(product);

                  return (
                    <div
                      key={product._id}
                      className="border border-gray-200 rounded-md overflow-hidden shadow-sm hover:shadow-md transition-shadow hover:border-orange-700 cursor-pointer"
                      onClick={() =>
                        handleNavigateToProductDetails(product._id)
                      }
                    >
                      <div className="relative">
                        {product.stock_status === "out of stock" && (
                          <span className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded">
                            Out Of Stock
                          </span>
                        )}

                        <div className="h-48 flex items-center justify-center bg-gray-100">
                          <img
                            src={product.images[0]}
                            alt={product.name}
                            className="h-full w-full object-cover"
                          />
                        </div>
                        <div className="absolute top-2 right-2 flex flex-col gap-2">
                          <button
                            className="p-1 rounded-full bg-white shadow hover:bg-gray-100"
                            onClick={(e) =>
                              handleToggleWishlist(e, product._id)
                            }
                            disabled={processingWishlist[product._id]}
                          >
                            {processingWishlist[product._id] ? (
                              <span className="block h-5 w-5 border-t-2 border-r-2 border-gray-700 rounded-full animate-spin"></span>
                            ) : isInWishlist(product._id) ? (
                              <AiFillHeart className="text-red-500" size={20} />
                            ) : (
                              <AiOutlineHeart
                                className="text-gray-500"
                                size={20}
                              />
                            )}
                          </button>
                          <button
                            className="p-1 rounded-full bg-white shadow hover:bg-gray-100"
                            onClick={(e) =>
                              handleToggleComparison(e, product._id)
                            }
                            disabled={processingComparison[product._id]}
                          >
                            {processingComparison[product._id] ? (
                              <span className="block h-5 w-5 border-t-2 border-r-2 border-gray-700 rounded-full animate-spin"></span>
                            ) : isInComparison(product._id) ? (
                              <MdOutlineBarChart
                                className="text-yellow-500"
                                size={20}
                              />
                            ) : (
                              <MdOutlineBarChart
                                className="text-gray-500"
                                size={20}
                              />
                            )}
                          </button>
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm text-gray-500 capitalize">
                          {product.category?.name || categoryName}
                        </h3>
                        <h2 className="font-medium text-gray-800 capitalize">
                          {product.name}
                        </h2>
                        <p className="text-sm text-gray-500 mt-1">
                          Brand:{" "}
                          {product.brand ? product.brand.trim() : "Unknown"}
                        </p>
                        {product?.measurement && (
                          <p className="font-medium text-gray-600 capitalize text-sm">
                            ({product.measurement})
                          </p>
                        )}

                        <div className="flex items-center justify-between mt-2">
                          <div>
                            <span className="font-bold text-gray-800">
                              ₹ {priceInfo.price}
                            </span>
                            {priceInfo.previousPrice && (
                              <span className="text-gray-500 line-through text-sm ml-2">
                                ₹ {priceInfo.previousPrice}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center text-yellow-400 mt-1">
                          {renderRatingStars(product.ratings || 0)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryWiseProduct;
